import React, { useEffect, useState, useContext } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { Logger } from 'aws-amplify';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { RootState } from 'src/store/reduxTypes';
import {
  selectFileChannel,
  createFileChannel,
  unselectFileChannel,
} from 'src/store/files/actions';
import { FILES_PAGE } from 'src/constants';
import { useChannelItemSetup } from 'src/components/Files/useChannelItemSetup';
import {
  setSecondarySidebarHidden,
  toggleSecondarySidebar,
} from 'src/store/ui/actions';
import { markFileChannelNotificationAsRead } from 'src/store/notifications/actions';
import { SecondarySideBarType } from 'src/store/ui/types';
import BaseChannelSidebar, {
  BaseChannelFormData,
} from 'src/components/Channels/BaseChannelSidebar';
import { ChannelOptionData } from 'src/components/Channels/ChannelSidebarItem';
import { PlusIcon, FolderIcon } from 'src/components/Icons';
import useNavigate from 'src/hooks/useNavigate';
import { FlagsContext, RouteContext } from 'src/context';
import { FileQueryParamProps } from 'src/store/files/types';
import { useCompanyClientsFilter } from 'src/components/Client/useCompanyClientsFilter';
import { useChannelNavigationShortcut } from 'src/components/Channels/useChannelNavigation';
import { channelOptionsSortFunction } from 'src/components/Files/utilities';
import { GetCurrentUser } from 'src/store/users/reducers';
import { ChannelsPageLayoutHeaderContext } from 'src/components/Layout';

const logger = new Logger('FilesSidebar');

export const FilesSidebar: React.FC = () => {
  const dispatch = useDispatch();
  const { navigate } = useNavigate();

  const {
    userId,
    isSuperUser,
    isClient,
    selectedChannel,
    channels,
    searchKey,
    channelName,
    isSidebarHidden,
    isOpen,
    clients,
    companies,
    notifications,
  } = useSelector(
    (state: RootState) => ({
      userId:
        GetCurrentUser({
          usersState: state.users,
          currentUserId: state.user.id,
          isClient: state.user.isClient,
        })?.id || '',
      isSuperUser: state.user.data?.superUser,
      havePlaceholderCompany: state.user.data?.havePlaceholderCompany,
      isClient: state.user.isClient,
      selectedChannel: state.files.selectedChannel,
      channels: state.files.channels,
      searchKey: state.ui.searchValue,
      channelName: state.ui.secondarySideBar.info,
      isSidebarHidden: state.ui.secondarySideBar.hidden,
      isOpen: state.ui.secondarySideBar.isOpen,
      clients: state.user.isClient
        ? state.users.users
        : state.clients.assignedClients,
      companies: state.user.isClient
        ? state.clients.companies
        : state.clients.activeCompanies,
      notifications: state.notifications,
    }),
    shallowEqual,
  );

  const { EnableFilesNotificationCenter } = React.useContext(FlagsContext);

  // we only want to allow marking notifications as read
  // when the current user is a client
  // or when flag for files notification center is turned off
  const allowMarkNotificationAsRead =
    isClient || !EnableFilesNotificationCenter;

  const createChannelItemOption = React.useCallback(
    useChannelItemSetup(clients, companies),
    [clients, companies],
  );
  const theme = useTheme();
  const { query } = useContext(RouteContext);
  const { channelId, selectedFilePath } = query as FileQueryParamProps;
  const isSmallWidth = useMediaQuery(theme.breakpoints.down('xs'), {
    noSsr: true,
  });

  const [filesChannelItemsList, setFileChannelItemsList] = useState<
    ChannelOptionData[]
  >([]);
  const [
    filteredChannelListItemOptions,
    setFilteredFileChannelItemsList,
  ] = useState<ChannelOptionData[]>([]);

  const currentSelectedChannelIndex = React.useMemo(
    () =>
      filteredChannelListItemOptions.findIndex(
        channel => channel.fileChannel?.id === channelId,
      ),
    [channelId, filteredChannelListItemOptions],
  );
  /**
   * Dispatch an action to change channel and to mark any file notifications that
   * correspond to the current channel item as read.
   * Note: If there are e-signature request complete notifications
   * in a channel, they should be marked as read first.
   */
  const onSelectChannel = (
    filesChannelSidebarItemOption: ChannelOptionData,
  ) => {
    const { fileChannel } = filesChannelSidebarItemOption;
    dispatch(selectFileChannel(fileChannel));
    if (fileChannel && allowMarkNotificationAsRead) {
      dispatch(markFileChannelNotificationAsRead(fileChannel.id));
    }
  };

  useChannelNavigationShortcut(
    filesChannelItemsList,
    currentSelectedChannelIndex,
    onSelectChannel,
  );
  /**
   * check if root path is selected, occurs when query param for selectedPath
   * has no value. Use callback since this is passed as prop to BaseChannelSidebar
   */
  const isRootPathSelected = React.useCallback(() => !selectedFilePath, [
    selectedFilePath,
  ]);

  // search companies and return all clients for matched companies
  const { filteredClientIds: companyClientIds } = useCompanyClientsFilter({
    searchKey,
  });

  const filterChannels = (filter: string, items: ChannelOptionData[]) => {
    const filteredChannelItems = items.filter(channelItemOption => {
      const companyMembers = channelItemOption.fileChannel?.additionalFields
        ?.memberIDs
        ? Object.keys(
            channelItemOption.fileChannel?.additionalFields?.memberIDs,
          ).filter(memberId => companyClientIds.includes(memberId))
        : [];

      return (
        (channelItemOption &&
          channelItemOption.name
            .toLowerCase()
            .includes(filter.toLowerCase())) ||
        companyMembers.length > 0
      );
    });

    setFilteredFileChannelItemsList(filteredChannelItems);
  };

  const {
    setActiveChannelTitle,
    setChannelsListToolbarHidden,
  } = React.useContext(ChannelsPageLayoutHeaderContext);
  /**
   * This effect listens for changes to selectedChannel and updates store
   * values for sidebar open and channel summary info that is shown in main content
   */
  useEffect(() => {
    if (selectedChannel) {
      const allChannels = [...filesChannelItemsList];
      const channelItem = allChannels.find(
        c => c.fileChannel?.id === selectedChannel.id,
      );

      if (
        selectedChannel.id !== channelId ||
        !channelName ||
        channelItem?.name !== channelName ||
        isSmallWidth
      ) {
        // on new layout updates, we set the channel title in the header using the channels page header context
        setActiveChannelTitle(channelItem?.name || channelName || '');
        // update sidebar info and related channel side-effects
        const secondarySidebarParams: SecondarySideBarType = {
          isOpen: !isSmallWidth,
          info: channelItem?.name || channelName,
        };

        dispatch(toggleSecondarySidebar(secondarySidebarParams));

        if (isSidebarHidden && allowMarkNotificationAsRead) {
          // when no sidebar is rendered we still need to mark notifications as read
          dispatch(markFileChannelNotificationAsRead(selectedChannel.id));
        }
        if (selectedChannel.id !== channelId) {
          // selected channel and channelId query param are different
          // update channelId query param
          navigate(`${FILES_PAGE.path}?channelId=${selectedChannel.id}`);
        }
      }
    }
  }, [selectedChannel, channelId, filesChannelItemsList, channelName]);

  /**
   * Ensure to clean up selected channel on unmount so there is no
   * pending channel in store
   */
  useEffect(
    () => () => {
      // on clean up remove selected file channel/items so that there is
      // no pending channel in state
      dispatch(unselectFileChannel());
    },
    [],
  );

  useEffect(() => {
    if (isSmallWidth && isOpen) {
      dispatch(selectFileChannel());
    }
  }, [isOpen]);

  /**
   * This method allows to sort channels options items by descending date
   * @param channelsOptions
   */
  const handleSortChannels = (channelsOptions: ChannelOptionData[]) => {
    const personalChannels = channelsOptions.filter(
      c => c.initialLetters && c.position === 1,
    );

    const sharedChannels = channelsOptions.filter(
      c => c.initialLetters && c.position === 2,
    );

    const channelsSortedByActivity = channelsOptions.sort(
      channelOptionsSortFunction(notifications),
    );

    const sortedChannelOptionList = [
      ...personalChannels,
      ...sharedChannels,
      ...channelsSortedByActivity,
    ];

    setFileChannelItemsList(sortedChannelOptionList);
    filterChannels(searchKey, sortedChannelOptionList);
  };

  const setupFileChannels = (selectFirstChannel: boolean) => {
    // match the channels returned from backend with either client user or company items
    // that are in store. If item is not in store the channel wont be rendered. If item is
    // found it is added to list of items to render
    logger.debug('Setting up file channels, select first:', selectFirstChannel);
    const filesChannelOptionList = [] as ChannelOptionData[];
    channels.forEach(channel => {
      if (!channel.fields) {
        console.error(`Channel ${channel.id} has no fields`);
        return;
      }
      const filesChannelMember =
        isSuperUser || channel.additionalFields?.memberIDs[userId];
      let channelOption = null;
      if (filesChannelMember) {
        channelOption = createChannelItemOption(channel);
      }
      if (channelOption) {
        filesChannelOptionList.push(channelOption);
      } else {
        logger.debug('Item options not available for channel:', channel.id);
      }
    });
    filesChannelOptionList.sort((a, b) => {
      if (a.position && b.position) {
        return a.position - b.position;
      }
      return 0;
    });

    logger.debug('Setup channel options', filesChannelOptionList);
    if (selectFirstChannel) {
      if (filesChannelOptionList.length === 1) {
        onSelectChannel(filesChannelOptionList[0]);
      }
    }
    handleSortChannels(filesChannelOptionList);
  };

  const handleSearchChanged = (searchInput: string) => {
    // check that the channel matches the search filter
    // if the search filter is empty then it will always match
    if (searchInput) {
      filterChannels(searchInput, filesChannelItemsList);
    }
  };

  React.useEffect(() => {
    if (searchKey.length < 1) {
      // when there is no search input and we are setting up the channels
      // decide to select the first channel if there is no current selected channel
      // and this is a condition where the first channel should be selected meaning
      // it is not a mobile page or the sidebar is hidden
      setupFileChannels(
        !selectedChannel && (!isSmallWidth || channels.length === 1),
      );
      return;
    }
    handleSearchChanged(searchKey);
  }, [searchKey]);

  /**
   * file channels need to be updated when clients or channels list are modified
   * when a channel is already selected or this is a client not on a small screen
   * then select the channel during setup
   */
  React.useEffect(() => {
    setupFileChannels(
      !selectedChannel && (!isSmallWidth || channels.length === 1),
    );
  }, [channels, clients, notifications]);

  /**
   * if no selectedChannel is selected, which is the case when selected channel is removed
   * then select the first channel in filtered chanel list
   * Also handles the case when selected channel is not present in filtered list, it occurs when logged in member is removed from the channel
   */
  React.useEffect(() => {
    const isSelectedChannelAccessible = filteredChannelListItemOptions.find(
      item => item.fileChannel?.id === selectedChannel?.id,
    );

    if (
      (!selectedChannel &&
        !channelId &&
        !isSmallWidth &&
        filteredChannelListItemOptions.length > 0) ||
      (filteredChannelListItemOptions.length > 0 &&
        !isSelectedChannelAccessible &&
        selectedChannel) // if selected channel is not present in filtered channel
    ) {
      onSelectChannel(filteredChannelListItemOptions[0]);
    }
  }, [filteredChannelListItemOptions, selectedChannel]);

  React.useEffect(
    () => () => {
      // reset files sidebar
      dispatch(
        toggleSecondarySidebar({
          isOpen: false,
        }),
      );
    },
    [],
  );

  /**
   * when the current user is a client then hide the sidebar when there is one or less channels
   * if the user is not a client then the sidebar should be hidden when there are no channels
   */
  const shouldHideSidebar = () =>
    filesChannelItemsList.length < (isClient ? 2 : 1);

  React.useEffect(() => {
    if (shouldHideSidebar()) {
      setChannelsListToolbarHidden(true);
      // no sidebar component is rendered when this is a client user with a placeholder company
      // or there are no channels
      dispatch(setSecondarySidebarHidden(true));
    } else {
      setChannelsListToolbarHidden(false);
      dispatch(setSecondarySidebarHidden(false));
      dispatch(
        toggleSecondarySidebar({
          isOpen: true,
        }),
      );
    }
  }, [filesChannelItemsList.length, isClient]);

  const handleCreateChannel = async (formData: BaseChannelFormData) => {
    // if company channels is enabled the form data should include memberIDs and their type
    const { membersType, memberIDs } = formData.fields;
    if (!memberIDs || !membersType) {
      console.error('memberIDs and membersType are required');
      return;
    }
    if (membersType === 'company') {
      await dispatch(createFileChannel({ companyID: memberIDs[0], clients }));
    } else {
      await dispatch(createFileChannel({ clientUserIDs: memberIDs, clients }));
    }
  };

  return shouldHideSidebar() ? null : (
    <BaseChannelSidebar
      entity="fileChannel"
      modalTitle="New files channel"
      onCreate={handleCreateChannel}
      initialCreateFormValue={{
        fields: {
          memberIDs: [],
          membersType: '',
        },
      }}
      sidebarItemOptions={filteredChannelListItemOptions}
      standardPageParams={{
        title: 'Create a channel to share files with clients',
        topIcon: <FolderIcon />,
        desc:
          'This is where you can see your files. To share files, first create a channel with your client.',
        startIconForActionButton: <PlusIcon />,
        labelForActionButton: 'Create a channel',
        onAction: handleCreateChannel,
        learnMoreLinkUrl:
          'https://www.joinportal.com/feature/secure-filesharing',
      }}
      onSelectChannel={onSelectChannel}
      isChannelRootSelected={isRootPathSelected}
    />
  );
};
